import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { CustomSelect } from "../ui/custom-select";
import api from "../../lib/api";
import { Loader2, CheckCircle2, AlertCircle, ChevronRight, Search, Package } from "lucide-react";

interface ProductMasterResult {
  id: number;
  drugname: string | null;
  drugcode: string | null;
  manufacturer: string | null;
  packform: string | null;
  drugform: string | null;
  mrp: string | null;
}

interface AddProductStockModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  entities: { value: number; label: string }[];
  /** Pre-selected entity id (from the current page context) */
  defaultEntityId?: number | null;
}

type Step = "pharmacy" | "product";

export default function AddProductStockModal({
  open,
  onClose,
  onSuccess,
  entities,
  defaultEntityId,
}: AddProductStockModalProps) {
  const [step, setStep] = useState<Step>("pharmacy");
  const [selectedEntityId, setSelectedEntityId] = useState<number | null>(
    defaultEntityId ?? null,
  );

  // Product search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<ProductMasterResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductMasterResult | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Debounce ref
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset on open/close
  useEffect(() => {
    if (open) {
      setStep(defaultEntityId ? "product" : "pharmacy");
      setSelectedEntityId(defaultEntityId ?? null);
      setSearchQuery("");
      setSearchResults([]);
      setSelectedProduct(null);
      setDropdownOpen(false);
      setError(null);
      setSuccess(false);
    }
  }, [open, defaultEntityId]);

  // Sync defaultEntityId changes while open
  useEffect(() => {
    if (open && defaultEntityId) {
      setSelectedEntityId(defaultEntityId);
    }
  }, [defaultEntityId, open]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Debounced product search
  useEffect(() => {
    if (selectedProduct && selectedProduct.drugname === searchQuery) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      return;
    }
    if (searchQuery.length < 2) {
      setSearchResults([]);
      setDropdownOpen(false);
      if (selectedProduct && selectedProduct.drugname !== searchQuery) {
        setSelectedProduct(null);
      }
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res: any = await api.searchProductsMasterForStock(searchQuery);
        const results = res?.data ?? [];
        setSearchResults(results);
        setDropdownOpen(true);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery, selectedProduct]);

  const handleSelectProduct = (product: ProductMasterResult) => {
    setSelectedProduct(product);
    setSearchQuery(product.drugname ?? "");
    setDropdownOpen(false);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!selectedEntityId || !selectedProduct) return;
    setSubmitting(true);
    setError(null);
    try {
      const res: any = await api.createProductStockEntry(selectedEntityId, selectedProduct.id);
      if (res?.success) {
        setSuccess(true);
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 1200);
      } else {
        setError(res?.error ?? "Failed to add product. Please try again.");
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || "An unexpected error occurred.");
    } finally {
      setSubmitting(false);
    }
  };

  const canProceedToStep2 = !!selectedEntityId;
  const canSubmit = !!selectedEntityId && !!selectedProduct && !submitting;

  const selectedEntityLabel = entities.find((e) => e.value === selectedEntityId)?.label ?? "";

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-[520px] p-0 overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-br from-blue-600 to-indigo-700 px-6 pt-6 pb-5">
          <DialogHeader>
            <DialogTitle className="text-white text-xl font-bold flex items-center gap-2">
              <Package size={20} className="text-blue-200" />
              Add Product to Stock
            </DialogTitle>
            <p className="text-blue-200 text-sm mt-1">
              Select a pharmacy, then search and pick a product from the master catalogue.
            </p>
          </DialogHeader>

          {/* Step indicators */}
          <div className="flex items-center gap-2 mt-4">
            {/* Step 1 pill */}
            <button
              type="button"
              onClick={() => { setStep("pharmacy"); setError(null); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                step === "pharmacy"
                  ? "bg-white text-indigo-700 shadow"
                  : "bg-white/20 text-white hover:bg-white/30"
              }`}
            >
              <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold ${
                step === "pharmacy" ? "bg-indigo-700 text-white" : selectedEntityId ? "bg-green-400 text-white" : "bg-white/40 text-white"
              }`}>
                {selectedEntityId && step !== "pharmacy" ? "✓" : "1"}
              </span>
              Pharmacy
            </button>

            <ChevronRight size={14} className="text-blue-300" />

            {/* Step 2 pill */}
            <button
              type="button"
              onClick={() => { if (canProceedToStep2) { setStep("product"); setError(null); } }}
              disabled={!canProceedToStep2}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                step === "product"
                  ? "bg-white text-indigo-700 shadow"
                  : "bg-white/20 text-white hover:bg-white/30"
              }`}
            >
              <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold ${
                step === "product" ? "bg-indigo-700 text-white" : "bg-white/40 text-white"
              }`}>
                2
              </span>
              Product
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 bg-white dark:bg-gray-900 min-h-[200px]">

          {/* ── STEP 1: Select Pharmacy ── */}
          {step === "pharmacy" && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Select Pharmacy <span className="text-red-500">*</span>
                </label>
                <CustomSelect
                  options={entities}
                  value={selectedEntityId as any}
                  onChange={(val: any) => setSelectedEntityId(val)}
                  placeholder="Choose a pharmacy..."
                />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                The product will be added to the selected pharmacy's stock.
              </p>
            </div>
          )}

          {/* ── STEP 2: Search & Select Product ── */}
          {step === "product" && (
            <div className={`space-y-4 transition-all duration-200 ${dropdownOpen ? "pb-60" : ""}`}>
              {/* Selected pharmacy badge */}
              <div className="flex items-center gap-2 bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700 rounded-lg px-3 py-2">
                <span className="text-xs text-zinc-500 dark:text-zinc-400 font-medium">Pharmacy:</span>
                <span className="text-xs font-semibold text-zinc-900 dark:text-zinc-100 truncate">{selectedEntityLabel}</span>
              </div>

              {/* Search input with dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Search Product <span className="text-red-500">*</span>
                </label>
                <div className="relative" ref={dropdownRef}>
                  <div className="relative">
                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    <Input
                      id="product-search"
                      placeholder="Type drug name or code (min 2 chars)..."
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setSelectedProduct(null);
                        setError(null);
                      }}
                      className="pl-9 pr-4"
                      autoComplete="off"
                    />
                    {searching && (
                      <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400" />
                    )}
                  </div>

                  {/* Dropdown results */}
                  {dropdownOpen && (
                    <div className="absolute z-50 left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-xl max-h-60 overflow-y-auto pb-2">
                      {searchResults.length === 0 ? (
                        <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                          No products found for "<span className="font-medium">{searchQuery}</span>"
                        </div>
                      ) : (
                        searchResults.map((product) => (
                          <button
                            key={product.id}
                            type="button"
                            onMouseDown={() => handleSelectProduct(product)}
                            className="w-full text-left px-4 py-3 hover:bg-zinc-100 dark:hover:bg-zinc-800/50 border-b border-gray-100 dark:border-gray-700 last:border-0 transition-colors group"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate group-hover:text-black dark:group-hover:text-white">
                                  {product.drugname ?? "—"}
                                </p>
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                                  {product.drugcode && (
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                      Code: <span className="font-mono font-medium">{product.drugcode}</span>
                                    </span>
                                  )}
                                  {product.packform && (
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                      {product.packform}
                                    </span>
                                  )}
                                  {product.manufacturer && (
                                    <span className="text-xs text-gray-400 dark:text-gray-500 truncate">
                                      {product.manufacturer}
                                    </span>
                                  )}
                                </div>
                              </div>
                              {product.mrp && (
                                <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 whitespace-nowrap">
                                  ₹{product.mrp}
                                </span>
                              )}
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Searches by drug name and drug code.
                </p>
              </div>

              {/* Selected product preview card */}
              {selectedProduct && (
                <div className="border border-emerald-200 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-3 space-y-2">
                  <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 uppercase tracking-wide">
                    Selected Product
                  </p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                    <div>
                      <span className="text-xs text-gray-500 dark:text-gray-400 block">Drug Name</span>
                      <span className="font-medium text-gray-800 dark:text-gray-100">{selectedProduct.drugname ?? "—"}</span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 dark:text-gray-400 block">Drug Code</span>
                      <span className="font-mono font-medium text-gray-800 dark:text-gray-100">{selectedProduct.drugcode ?? "—"}</span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 dark:text-gray-400 block">Pack Form</span>
                      <span className="text-gray-800 dark:text-gray-100">{selectedProduct.packform ?? "—"}</span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 dark:text-gray-400 block">Manufacturer</span>
                      <span className="text-gray-800 dark:text-gray-100">{selectedProduct.manufacturer ?? "—"}</span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 dark:text-gray-400 block">MRP</span>
                      <span className="text-gray-800 dark:text-gray-100">{selectedProduct.mrp ? `₹${selectedProduct.mrp}` : "—"}</span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 dark:text-gray-400 block">Drug Form</span>
                      <span className="text-gray-800 dark:text-gray-100">{selectedProduct.drugform ?? "—"}</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 dark:text-gray-500 pt-1 border-t border-emerald-200 dark:border-emerald-700">
                    Default stock will be set to <strong>10</strong>.
                  </p>
                </div>
              )}

              {/* Error / Success */}
              {error && (
                <div className="flex items-center gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg px-3 py-2.5">
                  <AlertCircle size={15} className="text-red-500 flex-shrink-0" />
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}
              {success && (
                <div className="flex items-center gap-2 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700 rounded-lg px-3 py-2.5">
                  <CheckCircle2 size={15} className="text-emerald-500 flex-shrink-0" />
                  <p className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">
                    Product added successfully!
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={submitting}
            className="text-gray-600 dark:text-gray-400"
          >
            Cancel
          </Button>

          <div className="flex items-center gap-2">
            {step === "pharmacy" && (
              <Button
                onClick={() => { setStep("product"); setError(null); }}
                disabled={!canProceedToStep2}
                className="bg-black text-white hover:bg-black/90 dark:bg-white dark:text-black dark:hover:bg-white/90 disabled:opacity-50"
              >
                Next
                <ChevronRight size={15} className="ml-1" />
              </Button>
            )}
            {step === "product" && (
              <Button
                onClick={handleSubmit}
                disabled={!canSubmit || success}
                className="bg-black text-white hover:bg-black/90 dark:bg-white dark:text-black dark:hover:bg-white/90 disabled:opacity-50 min-w-[120px]"
              >
                {submitting ? (
                  <span className="flex items-center gap-2">
                    <Loader2 size={14} className="animate-spin" />
                    Adding…
                  </span>
                ) : success ? (
                  <span className="flex items-center gap-2">
                    <CheckCircle2 size={14} />
                    Added!
                  </span>
                ) : (
                  "Add to Stock"
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
